import React from 'react';
import { Link } from 'react-router-dom';
import Container from './Container';
import Button from '../ui/Button';
import logo from '../../assets/logo/CodeMyAI Logo Exact Recreation.svg';

/**
 * Footer component for site-wide footer
 */
const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-900 text-white pt-16 pb-8">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center">
              <img src={logo} alt="CodeMyAI" className="h-28 w-auto" />
            </Link>
            <p className="mt-4 text-gray-300 max-w-md">
              Empowering businesses to harness the power of artificial intelligence through expert training, 
              seamless implementation, and continuous optimization.
            </p>
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Connect with us</h3>
              <div className="flex space-x-4">
                <a 
                  href="https://linkedin.com/company/CodeMyAI" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-blue-400 transition-colors duration-200"
                >
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
                
                <a 
                  href="https://github.com/CodeMyAI" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-blue-400 transition-colors duration-200"
                >
                  <span className="sr-only">GitHub</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/services/ai-training" className="text-gray-300 hover:text-blue-400 transition-colors duration-200">
                  AI Training & Education
                </Link>
              </li>
              <li>
                <Link to="/services/ai-onboarding" className="text-gray-300 hover:text-blue-400 transition-colors duration-200">
                  Business AI Onboarding
                </Link>
              </li>
              <li>
                <Link to="/services/workflow-automation" className="text-gray-300 hover:text-blue-400 transition-colors duration-200">
                  Workflow Automation
                </Link>
              </li>
              <li>
                <Link to="/services/custom-ai-development" className="text-gray-300 hover:text-blue-400 transition-colors duration-200">
                  Custom AI Development
                </Link>
              </li>
              <li>
                <Link to="/services/ai-optimization" className="text-gray-300 hover:text-blue-400 transition-colors duration-200">
                  AI System Optimization
                </Link>
              </li>
            </ul>
          </div>


          {/* Contact info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <svg className="h-6 w-6 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:Hello@CodeMyAI.com" className="text-gray-300 hover:text-blue-400 transition-colors duration-200">
                  Hello@CodeMyAI.com
                </a>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="tel:+4407415928896" className="text-gray-300 hover:text-blue-400 transition-colors duration-200">
                  +44 07415928896
                </a>
              </li>
              <li className="flex items-start">
                <svg className="h-6 w-6 mr-2 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-gray-300">
                  31 Giles Drive<br />
                  London, UK
                </span>
              </li>
            </ul>
            <div className="mt-6">
              <Button variant="primary" size="sm" onClick={() => window.location.href = '/contact'}>
                Book a Consultation
              </Button>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-700 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} CodeMyAI. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/legal/privacy-policy" className="hover:text-blue-400 transition-colors duration-200">
              Privacy Policy
            </Link>
            <Link to="/legal/terms-of-service" className="hover:text-blue-400 transition-colors duration-200">
              Terms of Service
            </Link>
            <Link to="/legal/cookies" className="hover:text-blue-400 transition-colors duration-200">
              Cookies
            </Link>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;